import api from '../../lib/api';

export const ASSISTANTS_LIST_LOAD = 'ASSISTANTS_LIST_LOAD';
export const ASSISTANT_LOAD = 'ASSISTANT_LOAD';
export const ASSISTANT_CREATE = 'ASSISTANT_CREATE';
export const ASSISTANT_UPDATE = 'ASSISTANT_UPDATE';
export const ASSISTANT_DELETE = 'ASSISTANT_DELETE';
export const ASSISTANT_SET_EMPTY = 'ASSISTANT_SET_EMPTY';
export const PROMPTS_LOAD = 'PROMPTS_LOAD';

const ASSISTANTS_LIST_LOADING_CHANGE = 'ASSISTANTS_LIST_LOADING_CHANGE';
const ASSISTANTS_LIST_CHANGE = 'ASSISTANTS_LIST_CHANGE';
const ASSISTANTS_LIST_TOTAL_CHANGE = 'ASSISTANTS_LIST_TOTAL_CHANGE';
const ASSISTANTS_LIST_PAGE_CHANGE = 'ASSISTANTS_LIST_PAGE_CHANGE';

export const ASSISTANT_CHANGE = 'ASSISTANT_CHANGE';
const ASSISTANT_LOADING_CHANGE = 'ASSISTANT_LOADING_CHANGE';
const ASSISTANT_SAVING_CHANGE = 'ASSISTANT_SAVING_CHANGE';
const ASSISTANT_ERRORS_CHANGE = 'ASSISTANT_ERRORS_CHANGE';

const PROMPTS_CHANGE = 'PROMPTS_CHANGE';
const PROMPTS_LOADING_CHANGE = 'PROMPTS_LOADING_CHANGE';
const PROMPTS_ERRORS_CHANGE = 'PROMPTS_ERRORS_CHANGE';

const assistant_empty = () => ({
  name: '',
  description: '',
  default_role: '',
});

const state = {
  assistants_list: [],
  assistants_list_total: 0,
  assistants_list_per_page: 20,
  assistants_list_page: 1,
  assistants_list_loading: false,

  assistant: null,
  assistant_loading: false,
  assistant_saving: false,
  assistant_errors: {},

  prompts_list: [],
  prompts_loading: false,
  prompts_errors: {},
};

const actions = {
  /**
   * Загружает список ассистентов с пагинацией
   * @param commit
   * @param state
   * @param {Object} [param] - Параметры
   * @param {number} [param.limit] - Количество на странице (max 100, default 20)
   * @param {number} [param.offset] - Смещение (default 0)
   * @param {Object} [param.options] - Дополнительные опции
   */
  [ASSISTANTS_LIST_LOAD]({ commit, state }, param = {}) {
    const limit = param.limit || 20;
    const offset = param.offset || 0;
    const options = param?.options || {};

    const params = {
      limit: Math.min(limit, 100),
      offset,
    };

    return api.list(
      'assistant.list',
      '',
      params,
      state,
      commit,
      ASSISTANTS_LIST_LOADING_CHANGE,
      ASSISTANTS_LIST_CHANGE,
      null,
      options,
    );
  },

  /**
   * Загружает конкретного ассистента по ID
   * @param commit
   * @param state
   * @param {Object} param - Параметры
   * @param {string} param.id - ID ассистента (UUID)
   * @param {Object} [param.options] - Дополнительные опции
   */
  [ASSISTANT_LOAD]({ commit, state }, param) {
    const id = param.id;
    const options = param.options || {};

    return api.load(
      'assistant',
      id,
      state,
      commit,
      ASSISTANT_LOADING_CHANGE,
      ASSISTANT_CHANGE,
      ASSISTANT_ERRORS_CHANGE,
      options,
    );
  },

  /**
   * Создает нового ассистента
   * @param commit
   * @param state
   * @param {Object} param - Параметры
   * @param {Object} param.model - Данные ассистента
   * @param {string} param.model.name - Название ассистента
   * @param {string} param.model.description - Описание ассистента
   * @param {string} param.model.default_role - Роль по умолчанию
   * @param {Object} [param.options] - Дополнительные опции
   */
  [ASSISTANT_CREATE]({ commit, state }, param) {
    const model = param.model;
    const options = param.options || {};

    return api.create(
      'assistant.create',
      '',
      model,
      state,
      commit,
      ASSISTANT_SAVING_CHANGE,
      ASSISTANT_CHANGE,
      ASSISTANT_ERRORS_CHANGE,
      options,
    );
  },

  /**
   * Обновляет существующего ассистента
   * @param commit
   * @param state
   * @param {Object} param - Параметры
   * @param {Object} param.model - Данные ассистента
   * @param {string} param.model.id - ID ассистента (UUID)
   * @param {string} param.model.name - Название ассистента
   * @param {string} param.model.description - Описание ассистента
   * @param {string} param.model.default_role - Роль по умолчанию
   * @param {Object} [param.options] - Дополнительные опции
   */
  [ASSISTANT_UPDATE]({ commit, state }, param) {
    const model = param.model;
    const options = param.options || {};

    return api.update(
      'assistant.update',
      model.id,
      model,
      state,
      commit,
      ASSISTANT_SAVING_CHANGE,
      ASSISTANT_CHANGE,
      ASSISTANT_ERRORS_CHANGE,
      options,
    );
  },

  /**
   * Удаляет ассистента (soft delete)
   * @param commit
   * @param state
   * @param {Object} param - Параметры
   * @param {string} param.id - ID ассистента (UUID)
   * @param {Object} [param.options] - Дополнительные опции
   */
  [ASSISTANT_DELETE]({ commit, state }, param) {
    const id = param.id;
    const options = param.options || {};

    return api.delete(
      'assistant.delete',
      id,
      state,
      commit,
      ASSISTANT_CHANGE,
      options,
    );
  },

  /**
   * Сбрасывает ассистента в пустое состояние
   * @param commit
   */
  [ASSISTANT_SET_EMPTY]({ commit }) {
    commit(ASSISTANT_CHANGE, assistant_empty());
  },

  /**
   * Валидирует данные ассистента перед отправкой
   * @param {Object} model - Данные ассистента
   * @returns {Object} Объект с ошибками валидации
   */
  validateAssistant(model) {
    const errors = {};

    if (!model.name || model.name.trim() === '') {
      errors.name = 'Название ассистента обязательно';
    }

    if (!model.description || model.description.trim() === '') {
      errors.description = 'Описание ассистента обязательно';
    }

    if (!model.default_role || model.default_role.trim() === '') {
      errors.default_role = 'Роль по умолчанию обязательна';
    }

    return errors;
  },

  /**
   * Загружает список промптов
   * @param commit
   * @param state
   * @param {Object} [param] - Параметры
   * @param {Object} [param.options] - Дополнительные опции
   */
  [PROMPTS_LOAD]({ commit, state }, param = {}) {
    const options = param?.options || {};

    return api.load(
      'llm.prompts',
      '',
      state,
      commit,
      PROMPTS_LOADING_CHANGE,
      PROMPTS_CHANGE,
      PROMPTS_ERRORS_CHANGE,
      options,
    );
  },
};

const mutations = {
  [ASSISTANTS_LIST_CHANGE](state, data) {
    const list = data?.data || data || [];
    state.assistants_list = Object.freeze(list.map((item) => Object.freeze(item)));
    state.assistants_list_total = data?.total || list.length;
  },

  [ASSISTANTS_LIST_LOADING_CHANGE](state, data) {
    state.assistants_list_loading = data;
  },

  [ASSISTANTS_LIST_TOTAL_CHANGE](state, data) {
    state.assistants_list_total = data || 0;
  },

  [ASSISTANTS_LIST_PAGE_CHANGE](state, data) {
    state.assistants_list_page = data;
  },

  [ASSISTANT_CHANGE](state, data) {
    state.assistant = data;
  },

  [ASSISTANT_LOADING_CHANGE](state, data) {
    state.assistant_loading = data;
  },

  [ASSISTANT_SAVING_CHANGE](state, data) {
    state.assistant_saving = data;
  },

  [ASSISTANT_ERRORS_CHANGE](state, data) {
    state.assistant_errors = data || {};
    state.assistant_loading = false;
    state.assistant_saving = false;
  },

  [PROMPTS_CHANGE](state, data) {
    state.prompts_list = data || [];
  },

  [PROMPTS_LOADING_CHANGE](state, data) {
    state.prompts_loading = data;
  },

  [PROMPTS_ERRORS_CHANGE](state, data) {
    state.prompts_errors = data;
  },
};

export default {
  namespaced: true,
  state,
  actions,
  mutations,
};
